<script lang="ts">
  import { onDestroy } from 'svelte';
  import { createQuery, createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { Plus, Copy } from 'lucide-svelte';
  import { adminApiKeysApi, type AdminApiKey } from '$lib/api/apiKeys';
  import { getErrorMessage } from '$lib/api/client';
  import Modal from '$lib/components/ui/Modal.svelte';

  const queryClient = useQueryClient();
  const keysQuery = createQuery(() => ({ queryKey: ['admin-api-keys'], queryFn: adminApiKeysApi.list }));
  let showForm = $state(false);
  let name = $state('');
  let plaintext = $state('');
  let pending = $state(false);
  let error = $state('');
  let copyMessage = $state('');
  let generation = 0;
  onDestroy(closeForm);

  function closeForm() {
    generation++;
    plaintext = '';
    name = '';
    error = '';
    copyMessage = '';
    showForm = false;
  }
  async function submit(event: SubmitEvent) {
    event.preventDefault();
    if (pending || !name.trim()) return;
    pending = true;
    error = '';
    const requestGeneration = generation;
    try {
      // Keep the one-time secret out of query/mutation caches and storage.
      const result = await adminApiKeysApi.create({ name: name.trim() });
      void queryClient.invalidateQueries({ queryKey: ['admin-api-keys'] });
      if (generation === requestGeneration) plaintext = result.key;
    } catch {
      // A lost response may still have created a key. Refresh metadata so
      // the administrator can revoke that unrecoverable key before retrying.
      void queryClient.invalidateQueries({ queryKey: ['admin-api-keys'] });
      if (generation === requestGeneration) error = 'Could not confirm key creation. Cancel to check the refreshed list and revoke any key whose plaintext you did not receive before retrying.';
    } finally {
      pending = false;
    }
  }
  async function copyKey() {
    const requestGeneration = generation;
    try {
      await navigator.clipboard.writeText(plaintext);
      if (generation === requestGeneration) copyMessage = 'Copied';
    } catch {
      if (generation === requestGeneration) copyMessage = 'Copy failed. Select and copy the key manually.';
    }
  }
  const revokeMutation = createMutation(() => ({
    mutationFn: adminApiKeysApi.revoke,
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: ['admin-api-keys'] }); }
  }));
  function revoke(key: AdminApiKey) {
    if (confirm(`Revoke API key "${key.name}"? Any integration using it will stop working.`)) revokeMutation.mutate(key.id);
  }
</script>

<div class="tym-page">
  <div class="mb-4 flex flex-wrap items-center gap-3">
    <div><h2 class="tym-section-title">API keys</h2><p class="tym-section-sub">Manage service integrations. Keep keys private.</p></div>
    <span class="flex-1"></span>
    <button type="button" class="btn-primary" disabled={pending} onclick={() => { closeForm(); showForm = true; }}><Plus class="h-4 w-4" /> New API key</button>
  </div>
  <p class="mb-4 text-sm text-dark-gray">One key grants read and write access across both integration APIs, including boards, task updates, nudges and delivery events—not employee or admin sessions. Send Authorization: Bearer &lt;key&gt;; no service header is needed. Configured IP restrictions still apply. Existing keys now have this same access. Environment tokens remain separate and are not listed or revoked here.</p>
  {#if revokeMutation.isError}<p class="tym-error-state">{getErrorMessage(revokeMutation.error)}</p>{/if}
  {#if keysQuery.isLoading}
    <p class="tym-loading-state">Loading API keys…</p>
  {:else if keysQuery.isError}
    <p class="tym-error-state">{getErrorMessage(keysQuery.error)}</p>
  {:else if keysQuery.data}
    <div class="tym-table-wrap"><div class="tym-table-scroll"><table class="tym-table">
      <thead><tr><th>Name</th><th>Prefix</th><th>Created</th><th>Last used</th><th>Status</th><th class="num"></th></tr></thead>
      <tbody>
        {#each keysQuery.data as key (key.id)}
          <tr>
            <td class="font-semibold text-navy">{key.name}</td><td><code>{key.prefix}</code></td>
            <td class="muted">{new Date(key.created_at).toLocaleString()}</td>
            <td class="muted">{key.last_used_at ? new Date(key.last_used_at).toLocaleString() : 'Never'}</td>
            <td>{key.revoked_at ? 'Revoked' : 'Active'}</td>
            <td class="num">{#if !key.revoked_at}<button type="button" class="btn-danger" disabled={revokeMutation.isPending} onclick={() => revoke(key)}>Revoke</button>{/if}</td>
          </tr>
        {:else}<tr><td class="empty" colspan="6">No API keys yet.</td></tr>{/each}
      </tbody>
    </table></div></div>
  {/if}
</div>

{#if showForm}
  <Modal title={plaintext ? 'API key created' : 'New API key'} onClose={closeForm} closable={!pending}>
    {#if plaintext}
      <div class="space-y-4">
        <p class="text-sm font-semibold text-navy">Copy this key now — you won’t see this again.</p>
        <p class="text-sm text-dark-gray">Send Authorization: Bearer &lt;key&gt; to either /api/intake/cos/* or /api/intake/cxo/*. The same key works for both; no X-Toyama-Service header is needed.</p>
        <label class="label" for="new-api-key">API key</label>
        <textarea id="new-api-key" class="input font-mono" readonly value={plaintext} rows="3" spellcheck="false"></textarea>
        <button type="button" class="btn-secondary" onclick={copyKey}><Copy class="h-4 w-4" /> Copy key</button>
        <p class="text-sm" role="status">{copyMessage}</p>
        <div class="flex justify-end"><button type="button" class="btn-primary" onclick={closeForm}>Done</button></div>
      </div>
    {:else}
      <form class="space-y-4" onsubmit={submit}>
        <div><label class="label" for="api-key-name">Name</label><input id="api-key-name" class="input" bind:value={name} required disabled={pending} maxlength="100" autocomplete="off" placeholder="Integration name" /></div>
        <p class="text-sm text-dark-gray">This grants read and write access across both integration APIs. The key is shown only once. IP restrictions still apply.</p>
        {#if error}<p class="text-sm text-red-600" role="alert">{error}</p>{/if}
        <div class="flex justify-end gap-2"><button type="button" class="btn-secondary" disabled={pending} onclick={closeForm}>Cancel</button><button type="submit" class="btn-primary" disabled={pending || !name.trim()}>{pending ? 'Creating...' : 'Create key'}</button></div>
      </form>
    {/if}
  </Modal>
{/if}
